import {
    centerCalculator,
    dateColumnSettings,
    formDataToObject,
    GestCard,
    ICity,
    ICommonDataGrid,
    iconColumnSettings,
    ICoordinates,
    IMarker,
    ISharedConfig,
    isMarkerInsidePolygon,
    setDialog,
    setSnackbar
} from '@shared/library';
import { languages } from '@shared/library/dist/static/language';
import axios, { AxiosError } from 'axios';
import moment from 'moment';
import React, { FormEvent } from 'react';
import config from '../../../../../../../config';
import importer from '../../../../../../../helpers/importer';
import localProps from '../../../../../../../helpers/localProps';
import './RouteGenerator.scss';
//#region COMPONENTS
import GestDataGrid from '@shared/library/dist/components/gest/GestDataGrid/GestDataGrid';
import openGestConfirmationDialog from '@shared/library/dist/components/gest/GestDialog/GestConfirmationDialog/GestConfirmationDialog';
import GestButton from '@shared/library/dist/components/gest/GestInputs/button/GestButton';
import GestSaveButton from '@shared/library/dist/components/gest/GestInputs/button/save/GestSaveButton';
import GestDatePicker from '@shared/library/dist/components/gest/GestInputs/pickers/date/GestDatePicker';
import GestSelect from '@shared/library/dist/components/gest/GestInputs/select/GestSelect';
import GestTextArea from '@shared/library/dist/components/gest/GestInputs/text area/GestTextArea';
import GestMap from '@shared/library/dist/components/gest/GestMap/GestMap';
import Title from '@shared/library/dist/components/gest/Title/Title';
import {
    CustomButton,
    CustomGroup,
} from '../../../../../../gest';
//#endregion
//#region MODELS
import { GridColDef, GridRowId } from '@mui/x-data-grid';
import { IGestDialogProps } from '@shared/library/dist/components/gest/GestDialog/GestDialogProps';
import { IGestButtonProps } from '@shared/library/dist/components/gest/GestInputs/button/GestButtonProps';
import { IGestSelectOption } from '@shared/library/dist/components/gest/GestInputs/select/GestSelectProps';
import { LangKey } from '../../../../../../../models/ui_data/Lang_model';
import { IMeterProcedure, IRoute } from './models/models';
//#endregion
//ICONS
import { AddCircle, CheckCircle, Edit, Info, Preview, RemoveCircle, Save, TableChart, Undo, Upload, UploadFile } from '@mui/icons-material';
import CenterFocusStrongRoundedIcon from '@mui/icons-material/CenterFocusStrongRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import { IQuery, metersColumns, setAndStoreSelectedCity } from '@webgest/library';

const RouteGenerator = (props: ISharedConfig) => {
    document.title = config.app.name;

    //#region CONTEXTS USE
    const lang = languages(props.settings?.lang);
    //#endregion

    //#region STATES
    //PREVIEW
    const previewModes: IGestSelectOption<undefined>[] = [
        { icon: <TableChart />, value: "Procedura", },
        { icon: <Upload />, value: "Upload file csv codici utente" }
    ];
    const [previewMode, setPreviewMode] = React.useState<IGestSelectOption<undefined>>(previewModes[0]);

    //PROCEDURES
    const [procedures, setProcedures] = React.useState<IGestSelectOption<IQuery>[]>([]);
    const [selectedProcedure, setSelectedProcedure] = React.useState<IGestSelectOption<IQuery>>();

    //METERS
    const [selectedMetersFromPolygon, setSelectedMetersFromPolygon] = React.useState<IMeterProcedure[]>([]);
    const [selectedMeters, setSelectedMeters] = React.useState<IMeterProcedure[]>([]);
    const [procedureResult, setProcedureResult] = React.useState<ICommonDataGrid<IMeterProcedure>>();
    const [importResult, setImportResult] = React.useState<ICommonDataGrid<IMeterProcedure>>();
    const [previewResult, setPreviewResult] = React.useState<ICommonDataGrid<IMeterProcedure>>();

    const procedureResultForm = React.useRef<HTMLFormElement>(null);
    //ROUTES
    const [routes, setRoutes] = React.useState<IRoute[]>([]);
    const [selectedRoute, setSelectedRoute] = React.useState<IRoute>();

    const cachedMarkersRef = React.useRef<any>({});
    const createForm = React.useRef<HTMLFormElement>(null);

    //MAP
    const [polygonCoords, setPolygonCoords] = React.useState<ICoordinates[]>([]);
    const [center, setCenter] = React.useState<ICoordinates>(config.env.map.defaultCenter);

    //FLAGS
    const [previewLoading, setPreviewLoading] = React.useState(false);
    const [createRoutesLoading, setCreateRoutesLoading] = React.useState(false);
    const [followPolygonCenter, setFollowPolygonCenter] = React.useState(localProps.panel.get('followPolygon') === 'true');
    //#endregion

    //#region METHODS

    //#region API CALLS
    //#region GET
    const getProceduresAPI = () => {
        return axios.get<IQuery[]>(`${config.api.internal}/RouteGenerator/Procedures`, { withCredentials: true })
            .then(res => {
                const opts = res.data.map(q => ({ value: q.id, label: q.name, data: q }));
                setProcedures(opts);
            })
            .catch((err: AxiosError) => {
                console.error(err);
                setSnackbar(props, { message: "Errore richiesta per procedure disponibili", severity: "error" });
            });
    };

    const getProcedurePreview = (event?: FormEvent, cityID?: number) => {
        event?.preventDefault();
        cityID = cityID ?? props.cache?.cities.selected?.value as number;

        if (selectedProcedure?.value && cityID) {
            let params = formDataToObject<{ [key: string]: string; }>(procedureResultForm.current);
            const cityParam = selectedProcedure.data?.parameters?.find(p => p.toLowerCase().includes('municipality') || (p.toLowerCase().startsWith('id') && p.toLowerCase().includes('tipoutenza')));

            if (cityParam) {
                params = { ...params, [cityParam]: cityID.toString() };
            }

            setPreviewLoading(true);
            return axios.get<ICommonDataGrid<any>>(`${config.api.internal}/RouteGenerator/Procedure/${selectedProcedure.value}/Preview`, { withCredentials: true, params })
                .then(res => {
                    const preview = res.data;
                    setProcedureResult(preview);

                    if (previewMode.value === previewModes[0].value) {
                        setPreviewResult(preview);
                    }
                })
                .catch((err: AxiosError) => {
                    console.error(err);
                    setSnackbar(props, { message: "Errore richiesta per anteprima della procedura", severity: "error" });
                })
                .finally(() => setPreviewLoading(false));
        };
    };
    //#endregion

    //#region POST
    const uploadPreview = (grid: ICommonDataGrid<IMeterProcedure>) => {
        if (grid.InitRows?.length) {
            const userIDs = grid.InitRows
                .slice(0, 1000)
                .map(m => m.codiceUtente ?? m.CodiceUtente);

            setPreviewLoading(true);
            return axios.post<IMeterProcedure[]>(`${config.api.internal}/RouteGenerator/Upload/Preview`, userIDs, { withCredentials: true })
                .then(res => {
                    //update grid content
                    const InitRows = res.data.map(m => ({ ...m, CodiceUtente: Number(m.codiceUtente) }));
                    grid = { InitRows };

                    setImportResult(grid);

                    if (previewMode.value === previewModes[1].value) {
                        setPreviewResult(grid);
                    }

                    if (!InitRows.length) {
                        setSnackbar(props, { message: "Nessun risultato utilizzando identificativi importati", severity: "warning" });
                    }
                    else {
                        //update city's options, selected city and selected meters
                        const city = props.cache?.cities.options?.find(c => InitRows.some(m => m.idTipoUtenza === c.value))!;
                        updateSelectedCity([city], InitRows);
                    }
                })
                .catch((err: AxiosError) => {
                    console.error(err);
                    setSnackbar(props, { message: "Errore durante upload nell'anteprima", severity: 'error' });
                })
                .finally(() => setPreviewLoading(false));
        }
        else setSnackbar(props, { message: "Nessuna riga valida importata dal csv", severity: 'error' });
    };

    const saveRoutes = () => {
        const newRoutes = routes
            .filter(r => r.meters?.length)
            .map(r => ({ ...r, meters: r.meters?.map(m => m.CodiceUtente) }));

        if (newRoutes.length) {
            setCreateRoutesLoading(true);
            return axios.post(`${config.api.internal}/RouteGenerator/Routes/Create`, newRoutes, { withCredentials: true })
                .then(() => {
                    clearRoutes();
                    setSnackbar(props, { message: lang.successfully_created, severity: 'success' });
                })
                .catch((err: AxiosError) => {
                    console.error(err);
                    setSnackbar(props, { message: "Errore durante generazione del percorso", severity: 'error' });
                })
                .finally(() => setCreateRoutesLoading(false));
        }
        else {
            setSnackbar(props, { message: "Nessun percorso valido creato", severity: 'error' });
        }
    };
    //#endregion
    //#endregion

    const createRoute = async (event: React.SubmitEvent<HTMLFormElement>) => {
        event.preventDefault();
        let newRoute = formDataToObject<IRoute>(createForm.current, true);

        if (newRoute && props.cache?.cities.selected) {
            //update routes
            newRoute.id = routes.length + 1;
            newRoute.meters = structuredClone(selectedMeters).map(m => ({ ...m, route_id: newRoute.id as number }));
            newRoute.cityID = props.cache.cities.selected.value as number;

            const withRemoved = routes.map(r => ({
                ...r,
                meters: r.meters?.filter(m => !newRoute.meters?.some(rm => rm.CodiceUtente === m.CodiceUtente))
            }));

            const updatedRoutes = [newRoute, ...withRemoved];
            setRoutes(updatedRoutes);

            //update meters
            const updatedMeters = previewResult?.InitRows?.map(m => ({
                ...m,
                route_id: (newRoute.meters!.some(nm => nm.CodiceUtente === m.CodiceUtente)
                    ? newRoute.id
                    : m.route_id) as number
            }));
            setPreviewResult({ ...previewResult, InitRows: updatedMeters });

            setDialog(props);
        }
    };

    const generateAllImportedRoutes = () => {
        if (previewMode.value === previewModes[1].value) {
            const now = moment();
            const newRoutes = props.cache!.cities.options
                .filter(c => previewResult?.InitRows?.some(m => m.idTipoUtenza === c.value))
                .map<IRoute>((c, i) => ({
                    id: routes.length + 1 + i,
                    meters: previewResult?.InitRows?.filter(m => m.idTipoUtenza === c.value) ?? [],
                    cityID: c.value as number,
                    startDate: now.toDate(),
                    endDate: now.add(2, "M").toDate(),
                    volume: `${(c.label as string).toLowerCase().substring(0, 3)}${(now.month() + 1).toString().length === 1 ? "0" : ""}${now.month() + 1}`,
                    name: `${c.label} ${now.format("DD/MM/YYYY HH:mm:ss")}`
                }));
            const oldRoutes = routes.map(r => ({
                ...r,
                meters: r.meters?.filter(m => !newRoutes.some(nr => nr.meters.some(nrm => nrm.CodiceUtente === m.CodiceUtente)))
            }));
            const updatedRoutes = [...newRoutes, ...oldRoutes];

            setRoutes(updatedRoutes);

            const updatedMeters = previewResult?.InitRows?.map(m => ({
                ...m,
                route_id: updatedRoutes.find(nr => nr.meters.some(nrm => nrm.CodiceUtente === m.CodiceUtente))?.id as number
            }));
            setPreviewResult({ ...previewResult, InitRows: updatedMeters });
        }
    };

    const assignToSelectedRoute = () => {
        //assign to existent route
        let updatedRoutes = structuredClone(routes);

        updatedRoutes.forEach(r => {
            r.meters = r.id === selectedRoute?.id
                ? selectedMeters
                : r.meters?.filter(m => !selectedMeters.some(sm => sm.CodiceUtente === m.CodiceUtente));
        });
        setRoutes(updatedRoutes);
        //update route_id in meters
        const updatedMeters = previewResult?.InitRows?.map(m => ({
            ...m,
            route_id: updatedRoutes.find(r => r.meters?.some(rm => rm.CodiceUtente === m.CodiceUtente))?.id as number
        }));
        setPreviewResult({ ...previewResult, InitRows: updatedMeters });
    };

    const clearRoutes = (routeID?: number) => {
        //flush routes
        setRoutes(!routeID ? [] : routes.filter(r => r.id !== routeID));
        //flush meters
        const InitRows = previewResult?.InitRows?.map(v => ({ ...v, route_id: !routeID || routeID === v.route_id ? undefined : v.route_id }));
        setPreviewResult({ ...previewResult, InitRows });

        if (!routeID || selectedRoute?.id === routeID) {
            setSelectedMeters([]);
        }
        //flush markers
        cachedMarkersRef.current = {};
        setDialog(props, undefined);
    };

    const resetPolygonSelection = () => {
        setSelectedMetersFromPolygon([]);
        setPolygonCoords([]);
    };

    const updateSelectedCity = (selected: IGestSelectOption<ICity>[], rows?: IMeterProcedure[]) => {
        setAndStoreSelectedCity(props, selected[0]);

        if (selectedProcedure?.value) {
            //re-execute procedure
            getProcedurePreview(undefined, selected[0].value as number);
        }

        const meters = selectedRoute?.meters ?? (rows ?? previewResult?.InitRows)?.filter(m => m.idTipoUtenza === selected[0].value) ?? [];
        setSelectedMeters(meters);
    };

    const updateMetersSelection = (ids: GridRowId[]) => {
        if (!ids.length) {
            setPolygonCoords([]);
        }

        if (!selectedRoute || selectedRoute.cityID === props.cache?.cities.selected?.value) {
            const selected = previewResult?.InitRows?.filter(m => ids.includes(m.CodiceUtente)) ?? [];
            setSelectedMeters(selected);
        }
        else {
            setSnackbar(props, { message: "Impossibile inserire in un giro comuni diversi", severity: "error" });
        }
    };

    //#region MODALS
    const openCreateRouteModal = () => {
        if (props.cache?.cities.selected?.value) {
            const name = `${props.cache.cities.selected.label} ${moment().format("DD/MM/YYYY HH:mm:ss")}`;
            const volume = `${(props.cache.cities.selected.label as string).toLowerCase().substring(0, 3)}${(moment().month() + 1).toString().length === 1 ? "0" : ""}${moment().month() + 1}`;
            const dialog: IGestDialogProps = {
                label: lang.create_route,
                content:
                    <form ref={createForm} onSubmit={createRoute} style={{ width: "300px" }}>
                        <GestTextArea name="name" label={lang.name} required fullw disabled value={name} />
                        <GestTextArea name="volume" label={"Volume"} maxLen={5} alphaNumeric required fullw defaultValue={volume} />

                        <div className='flex'>
                            <GestDatePicker name="startDate" label={lang.start_date} defaultValue={new Date()} required fullw />
                            <GestDatePicker name="endDate" label={lang.end_date} defaultValue={moment(new Date()).add(2, "M").toDate()} required fullw />
                        </div>

                        <GestSaveButton label={lang.save} submit fullw />
                    </form>
            };
            setDialog(props, dialog);
        }
    };

    const openRouteUsersModal = (row: IRoute) => setDialog(props, {
        label: "Utenze percorso " + row.name,
        content:
            <GestDataGrid
                columns={row.meters.some(m => m.idTipoUtenza)
                    ? metersColumns(lang) as GridColDef<IMeterProcedure>[]
                    : previewResult?.Columns ?? []
                }
                rows={row.meters}
                tablePropID='CodiceUtente'
                w={"55vw"}
                h={"60vh"}
                columnVisibilityModel={{ route_id: false }}
            />
    });

    const openClearRoutesModal = async () => await openGestConfirmationDialog(modal => props.set?.({ ...props, dialog: modal }), lang, "Rimuovere tutti i percorsi creati?", true) && clearRoutes();

    const openSaveRoutesModal = async () => await openGestConfirmationDialog(modal => props.set?.({ ...props, dialog: modal }), lang, "Vuoi salvare i percorsi creati?") && saveRoutes();
    //#endregion

    //#endregion

    //#region STATES CHANGE
    React.useEffect(() => { getProceduresAPI(); }, []);

    React.useEffect(() => {
        if (props.cache?.cities.options?.length && (props.cache.cities.selected?.value ?? -1) === -1) {
            setAndStoreSelectedCity(props, props.cache!.cities.options[0]);
        }
    }, [props.cache?.cities]);

    React.useEffect(() => {
        const selected = previewResult?.InitRows?.filter(m => isMarkerInsidePolygon({ lat: m.lat ?? m.latitudine, lng: m.lng ?? m.longitudine }, polygonCoords)) ?? [];
        setSelectedMetersFromPolygon(selected);
    }, [polygonCoords]);

    React.useEffect(() => {
        const newCenter = (polygonCoords.length > 2 && followPolygonCenter)
            ? centerCalculator([polygonCoords])
            : props.cache?.cities.selected?.data
                ? centerCalculator(props.cache.cities.selected.data.geoArea)
                : config.env.map.defaultCenter;

        if (newCenter && (newCenter.lat !== center.lat || newCenter.lng !== center.lng)) {
            setCenter(newCenter);
        };
    }, [props.cache?.cities.selected, polygonCoords, center]);
    //#endregion

    //#region RENDER
    const routeColumns = React.useMemo<GridColDef<IRoute>[]>(() => [
        { field: 'id', hideable: false },
        iconColumnSettings('delete', <RemoveCircle color='error' />, lang.remove, params => params.row.id && clearRoutes(params.row.id as number)),
        iconColumnSettings('showDetails', params => params.row.meters?.length ? <Info /> : undefined, "Visualizza utenze assegnate", params => params.row.meters?.length ? openRouteUsersModal(params.row) : undefined),
        {
            field: 'cityID',
            headerName: lang.municipality,
            valueGetter: (_, row) => props.cache?.cities.options?.find(c => c.value === row.cityID)?.label,
            renderCell: params => props.cache?.cities.options?.find(c => c.value === params.row.cityID)?.label,
            flex: 1,
        },
        {
            field: 'name',
            headerName: lang.name,
            width: 210,
        },
        {
            field: 'volume',
            headerName: "Volume",
            maxWidth: 70,
        },
        dateColumnSettings("startDate", lang.start_date),
        dateColumnSettings("endDate", lang.end_date),
        {
            field: 'meters',
            headerName: 'N. Utenze',
            align: "center",
            renderCell: params => params.row.meters?.length ?? 0
        },
    ], [lang]);

    const mapButtons: IGestButtonProps[] = React.useMemo(() => [
        {
            tooltip: lang.follow_center,
            icon: <CenterFocusStrongRoundedIcon />,
            onclick: () => {
                const newValue = !followPolygonCenter;
                localProps.panel.set('followPolygon', newValue.toString());
                setFollowPolygonCenter(newValue);
            },
        },
        {
            icon: <Undo />,
            disabled: !polygonCoords.length,
            tooltip: `Reset ${lang.polygon}`,
            onclick: resetPolygonSelection,
        },
        {
            icon: <RemoveCircle />,
            disabled: !selectedMetersFromPolygon.filter(sfp => selectedMeters.some(sm => sfp.CodiceUtente === sm.CodiceUtente)).length,
            tooltip: `${lang.remove_selected} (${selectedMetersFromPolygon.filter(sfp => selectedMeters.some(sm => sfp.CodiceUtente === sm.CodiceUtente)).length})`,
            onclick: () => setSelectedMeters(selectedMeters.filter(sm => !selectedMetersFromPolygon.some(sfp => sfp.CodiceUtente === sm.CodiceUtente)))
        },
        {
            icon: <AddCircle />,
            disabled: !selectedMetersFromPolygon.filter(sfp => !selectedMeters.some(sm => sfp.CodiceUtente === sm.CodiceUtente)).length,
            tooltip: `${lang.add_selected} (${selectedMetersFromPolygon.filter(sfp => !selectedMeters.some(sm => sfp.CodiceUtente === sm.CodiceUtente)).length})`,
            onclick: () => setSelectedMeters([...selectedMeters, ...selectedMetersFromPolygon])
        },
    ], [selectedMetersFromPolygon, followPolygonCenter, selectedMeters, lang]);

    const metersTableButtons: IGestButtonProps[] = React.useMemo(() => [
        {
            disabled: Boolean(previewMode.value !== previewModes[1].value || !importResult?.InitRows?.length),
            tooltip: "Genera tutti i percorsi caricati",
            icon: <CheckCircle />,
            onclick: generateAllImportedRoutes,
        },
        {
            disabled: !selectedMeters.length,
            tooltip: lang.add_to_new_route,
            icon: <AddCircle />,
            onclick: openCreateRouteModal,
        },
        {
            disabled: !selectedRoute || props.cache?.cities.selected?.value !== selectedRoute.cityID,
            tooltip: !selectedRoute || props.cache?.cities.selected?.value !== selectedRoute.cityID ? "Selezionare un percorso con lo stesso comune selezionato" : "Applica a percorso esistente",
            icon: <Edit />,
            onclick: assignToSelectedRoute,
        },
    ], [previewResult, selectedRoute, props.cache?.cities.selected, selectedMeters, lang]);
    //#endregion

    return (
        <div id="routeGenerator">
            <main>
                <Title size="xl" class='centered justify-content-between'>
                    {lang.route_generator}

                    <GestSelect
                        w={250}
                        tooltip={previewMode.value === previewModes[1].value
                            ? 'Importare dei codici utente e seleziona un comune su cui creare il giro'
                            : undefined
                        }
                        required
                        value={props.cache?.cities.selected?.value ?? ""}
                        label={lang.municipality}
                        onchange={updateSelectedCity}
                        options={props.cache?.cities.options?.filter(c =>
                            previewMode.value !== previewModes[1].value ||
                            previewResult?.InitRows?.some(m => m.idTipoUtenza === c.value)
                        )}
                    />
                </Title>

                <GestCard
                    title={lang.preview}
                    actions={<div className='flex'>
                        {/* procedure execution */}
                        <form
                            style={{ display: previewMode.value === previewModes[0].value ? "flex" : "none" }}
                            className='flex-row fitContent'
                            ref={procedureResultForm}
                            onSubmit={getProcedurePreview}
                        >
                            {selectedProcedure?.data?.parameters
                                ?.filter(p => !(p.toLowerCase().includes('municipality') || (p.toLowerCase().startsWith('id') && p.toLowerCase().includes('tipoutenza'))))
                                .map(p => {
                                    const paramName = p.replaceAll('@', '');
                                    return p.toLowerCase().includes('date') || p.toLowerCase().includes('data')
                                        ? <GestDatePicker
                                            name={p}
                                            label={lang[paramName as LangKey] ?? paramName}
                                            required
                                        />
                                        : <GestTextArea name={p} label={lang[paramName as LangKey] ?? paramName} required />;
                                })}

                            <GestSelect
                                w={300}
                                required
                                label={lang.procedures}
                                tooltip={!props.cache?.cities.selected?.value ? "Selezionare un comune" : undefined}
                                disabled={!props.cache?.cities.selected?.value}
                                value={selectedProcedure?.value}
                                options={procedures}
                                onchange={selected => setSelectedProcedure(selected[0])}
                            />

                            <GestButton
                                iconBtn
                                icon={<Preview />}
                                tooltip={lang.preview}
                                submit
                                disabled={!props.cache?.cities.selected?.value || !selectedProcedure?.value}
                            />
                        </form>

                        {/* import from csv */}
                        {previewMode.value === previewModes[1].value &&
                            <GestButton
                                iconBtn
                                icon={<Upload />}
                                tooltip={<>Carica csv codici utente. <br />Nome colonna obbligatorio: codiceUtente</>}
                                onupload={uploadPreview}
                            />}

                        <CustomGroup variant="union-shadowed">
                            <CustomButton
                                variant="light"
                                title={`Utenze da procedura`}
                                active={previewMode.value === previewModes[0].value}
                                onClick={() => {
                                    setPreviewMode(previewModes[0]);
                                    setPreviewResult(procedureResult);
                                }}
                            >
                                <TableChart />
                            </CustomButton>

                            <CustomButton
                                variant="light"
                                title={`Utenze da csv`}
                                active={previewMode.value === previewModes[1].value}
                                onClick={() => {
                                    setPreviewMode(previewModes[1]);
                                    setPreviewResult(importResult);
                                }}
                            >
                                <UploadFile />
                            </CustomButton>
                        </CustomGroup>
                    </div>}
                >
                    <GestDataGrid
                        rows={previewResult?.InitRows
                            ?.filter(m => !m.idTipoUtenza || m.idTipoUtenza === props.cache?.cities.selected?.value)
                            .map(m => ({ ...m, route_id: routes.find(r => r.meters.some(rm => rm.CodiceUtente === m.CodiceUtente))?.id as number }))
                        }
                        columns={[
                            {
                                field: 'route_id',
                                headerName: "Percorso",
                                width: 210,
                                valueGetter: (_, row) => routes.find(r => r.id === row.route_id)?.name,
                                renderCell: params => routes.find(r => r.id === params.row.route_id)?.name
                            },
                            ...(previewMode.value === previewModes[1].value
                                ? metersColumns(lang) as GridColDef<IMeterProcedure>[]
                                : previewResult?.Columns?.map(c => ({ ...c, headerName: lang[c.field as LangKey] ?? lang[c.field.toLowerCase() as LangKey], flex: 1 })) ?? []
                            )
                        ]}

                        selectedRowIDs={selectedMeters.map(m => m.CodiceUtente)}
                        updateSelection={updateMetersSelection}

                        loading={previewLoading || createRoutesLoading}
                        toolbar={metersTableButtons}
                        tablePropID='CodiceUtente'
                        w="55vw"
                    />
                </GestCard>

                <GestCard class="plansTable" title={lang.generated_routes}>
                    <GestDataGrid
                        loading={createRoutesLoading}
                        columns={routeColumns}
                        rows={routes}
                        w="55vw"
                        selectedRowIDs={selectedRoute?.id ?? []}
                        onRowClick={params => {
                            const firstClick = !selectedRoute;
                            const unselected = !firstClick && selectedRoute && params.row.id === selectedRoute.id;
                            setSelectedRoute(unselected ? undefined : params.row);

                            if (!firstClick || params.row.meters.length) {
                                setSelectedMeters(unselected ? [] : params.row.meters);
                            }
                        }}
                        disableSingleRowSelection={false}
                        toolbar={[
                            {
                                icon: <RestartAltRoundedIcon />,
                                tooltip: "Rimuovi tutti i percorsi",
                                onclick: openClearRoutesModal,
                                disabled: !routes.length,
                            },
                            {
                                tooltip: !routes.some(r => r.meters?.length) ? 'Inserire utenze nei giri per salvare' : lang.save,
                                icon: <Save />,
                                onclick: openSaveRoutesModal,
                                loading: createRoutesLoading,
                                disabled: !routes.some(r => r.meters?.length),
                            },
                        ]}
                    />
                </GestCard>

                <GestCard class="map">
                    <GestMap
                        loading={createRoutesLoading || previewLoading}
                        center={center}
                        zoom={13}
                        buttons={mapButtons}
                        tips={[lang.press_ctrl_and_click_to_draw_selection_area]}
                        polygons={{
                            areas: [{ points: props.cache?.cities.selected?.data?.geoArea }],
                            draw: { coords: polygonCoords, set: setPolygonCoords }
                        }}
                        onClick={event => {
                            event.stop();

                            if ((event.domEvent as KeyboardEvent).ctrlKey && event.latLng) {
                                const tmp = [...polygonCoords, { lat: event.latLng.lat(), lng: event.latLng.lng() }];
                                setPolygonCoords(tmp);
                            }
                        }}

                        markers={previewResult?.InitRows
                            ?.filter(m => !m.idTipoUtenza || m.idTipoUtenza === props.cache?.cities.selected?.value)
                            .map<IMarker<IMeterProcedure>>(m => ({
                                id: m.CodiceUtente,
                                model: m,
                                options: {
                                    position: { lat: m.lat ?? m.latitudine, lng: m.lng ?? m.longitudine },
                                    icon: importer.ic.require(`marker${selectedMeters.some(sm => sm.CodiceUtente === m.CodiceUtente) ? "Green"
                                        : selectedMetersFromPolygon.some(sfp => sfp.CodiceUtente === m.CodiceUtente) ? "Banana"
                                            : 'Blue'
                                        }.png`
                                    ),
                                    onClick: () => {
                                        const isSelected = selectedMeters.some(sm => sm.CodiceUtente === m.CodiceUtente);
                                        const meters = isSelected
                                            ? selectedMeters.filter(sm => sm.CodiceUtente !== m.CodiceUtente)
                                            : [...selectedMeters, m];

                                        setSelectedMeters(meters);
                                    }
                                }
                            })) ?? []}
                    />
                </GestCard>
            </main>
        </div >
    );
};
export default React.memo(RouteGenerator);